import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PsfPromoteOrShareDataQuery } from './api.graphql';
import { normalizeJoinTheCommunityCustomConfig } from './normalize/normalizeJoinTheCommunityCustomConfig';
import type {
	FcmValues,
	Petition,
	PromoteShareOnComboExperiment,
	PromotionStats,
	PsfPromoteOrSharePageData,
	Recruiter,
	Viewer,
} from './types';

type Experiments = PsfPromoteOrShareDataQuery['experiments'];

const CHIP_IN_USD_FALLBACK = 3;
const CHIP_IN_USD_SHARE_EXPERIMENT_AMOUNT = 5;

type PsfComboBanditData = {
	amount: number;
	currency: string;
	amountLocalized: string;
	amountId: string;
};

/**
 * FIXME this hard-coded value might have been set as a temporary value for estimating sign count from shares
 * until that metric would be properly computed in our backend, which seems to never have been properly planned
 * or prioritized. This was actually copied from the PSF Share page page data context.
 */
const SIGNS_FROM_SHARE_ESTIMATION_PERCENTAGE = 0.6;

function normalizeExperimentFlags(experiments: Experiments) {
	return {
		whatsappHeadlineExperiment: {
			variation: experiments.psfWhatsappShareCopyHeadline.variation,
			name: experiments.psfWhatsappShareCopyHeadline.experimentName,
			variants: experiments.psfWhatsappShareCopyHeadline.config,
		},
		advocateEntryPointExperiment: {
			variation: experiments.advocateEntryPoint.variation,
			name: experiments.advocateEntryPoint.experimentName,
		},
		promoteShareOnComboExperiment: {
			variation: experiments.promoteShareOnComboExperiment.variation,
		},
		revenueLongerComboCopyExperiment: {
			variation: experiments.revenueLongerComboCopyExperiment.variation,
		},
	};
}

// a type guard function, helps typescript to safely narrow the type in subsequent code
function isPsfComboBanditData(data: unknown): data is PsfComboBanditData {
	return (
		typeof data === 'object' &&
		data !== null &&
		'amount' in data &&
		typeof data.amount === 'number' &&
		'amountLocalized' in data &&
		typeof data.amountLocalized === 'string'
	);
}

function isPromoteShareActionVariation(variation: PromoteShareOnComboExperiment['variation']): boolean {
	return variation === 'variant_1' || variation === 'variant_2' || variation === 'variant_3';
}

type Params = {
	petition: Petition;
	promotionStats: PromotionStats;
	viewer: Viewer;
	experiments: Experiments;
	recruiter?: Recruiter;
	featureConfigs: FcmValues;
};

export function normalizeData(
	utilityContext: UtilityContext,
	{ petition, promotionStats, viewer, experiments, recruiter, featureConfigs }: Params,
): Omit<PsfPromoteOrSharePageData, 'psfComboRemoveAutoProgressionExperiment'> {
	const {
		bandits: { psfComboStandalone },
		...partialPetition
	} = petition;

	const promoteShareVariation = experiments.promoteShareOnComboExperiment.variation;
	const psfComboBandit = getPsfComboBanditData(
		psfComboStandalone,
		featureConfigs,
		utilityContext,
		promoteShareVariation,
	);
	const { tagsConnection, ...rest } = partialPetition;
	const normalizedCategoryTags = tagsConnection?.nodes.map((tag) => tag.name) ?? [];

	const signaturesFromShareCountEstimate = Math.round(
		petition.signatureState.signatureCount.displayed * SIGNS_FROM_SHARE_ESTIMATION_PERCENTAGE,
	);
	const experimentFlags = normalizeExperimentFlags(experiments);

	return {
		petition: { ...rest, promotionStats, categoryTags: normalizedCategoryTags },
		recruiter,
		bandits: { psfCombo: psfComboBandit },
		joinTheCommunityCustomConfig: normalizeJoinTheCommunityCustomConfig(utilityContext, {
			viewer,
			fcmValues: featureConfigs,
		}),
		...normalizeAmount({
			utilityContext,
			psfComboBandit,
			fcmValues: featureConfigs,
			viewer,
			promoteShareVariation,
		}),
		...normalizePromotionGoal(utilityContext, promotionStats.promoterCount),
		...experimentFlags,
		hideRecruiterEnabled: featureConfigs.hideRecruiterEnabled,
		isShareSlideUpEnabled: featureConfigs.isShareSlideUpEnabled,
		isShareAssetsEnabled: featureConfigs.isShareAssetsEnabled,
		useDonationLanguage: featureConfigs.useDonationLanguage,
		advocateEntryPointButtonLabel: featureConfigs.advocateEntryPointConfig.buttonLabel,
		advocateEntryPointCpDisabled: featureConfigs.advocateEntryPointConfig.disabledCps.includes(petition.slug),
		signaturesFromShareCountEstimateLocalized: utilityContext.i18n.localizeNumber(signaturesFromShareCountEstimate),
		promoteFormRawData: {
			viewer,
			featureConfigs,
		},
	};
}

function getPsfComboBanditData(
	psfComboStandalone: Petition['bandits']['psfComboStandalone'],
	fcmValues: PsfPromoteOrShareDataQuery['featureConfigs'],
	utilityContext: UtilityContext,
	promoteShareVariation: PromoteShareOnComboExperiment['variation'],
) {
	if (isPromoteShareActionVariation(promoteShareVariation)) {
		return {
			id: '',
			experimentName: '',
			variant: null,
			overridden: false,
			comboExpVariant: undefined,
		};
	}
	const { banditComboExpConfig } = fcmValues;
	const { cookies } = utilityContext;
	// We are considering dynamic bandit amounts as control since that's status quo and the
	// predetermined amounts as the variants. This is because the predetermined amounts are being tested.
	const PSF_COMBO_BANDIT_R7 = 'psf_combo_bandit_r7';

	// Generate a random number between 1 and 100 to determine the traffic percentage
	const psfComboExpPercentage = Math.floor(Math.random() * 100);

	let psfComboBanditVariation;
	if (banditComboExpConfig?.enabled) {
		if (
			cookies.get(PSF_COMBO_BANDIT_R7) === 'psf_combo_bandit_r7_static' ||
			psfComboExpPercentage <= banditComboExpConfig.treatment_traffic_percentage
		)
			// Static Bandit Values
			psfComboBanditVariation = 'static';
		else psfComboBanditVariation = 'control'; // Original Bandit
	}

	if (psfComboBanditVariation === 'static') {
		return {
			id: psfComboStandalone.id,
			experimentName: psfComboStandalone.experimentName,
			variant: null,
			overridden: false,
			comboExpVariant: `${PSF_COMBO_BANDIT_R7}_static`,
		};
	}

	return {
		id: psfComboStandalone.id,
		experimentName: psfComboStandalone.experimentName,
		...(psfComboStandalone.variants?.overrideVariant
			? { variant: psfComboStandalone.variants.overrideVariant, overridden: true }
			: { variant: psfComboStandalone.variant, overridden: false }),
		comboExpVariant: psfComboBanditVariation ? `${PSF_COMBO_BANDIT_R7}_${psfComboBanditVariation}` : undefined,
	};
}

function normalizeAmount({
	utilityContext,
	psfComboBandit,
	fcmValues,
	viewer,
	promoteShareVariation,
}: {
	utilityContext: UtilityContext;
	psfComboBandit: PsfPromoteOrSharePageData['bandits']['psfCombo'];
	fcmValues: PsfPromoteOrShareDataQuery['featureConfigs'];
	viewer: Viewer;
	promoteShareVariation: PromoteShareOnComboExperiment['variation'];
}) {
	const { banditComboExpConfig } = fcmValues;
	const { i18n } = utilityContext;
	const viewerCurrency = viewer.country.currency || { code: 'USD', exchangeRate: 1 };

	if (isPromoteShareActionVariation(promoteShareVariation)) {
		const chipInAmount = CHIP_IN_USD_SHARE_EXPERIMENT_AMOUNT;
		return { chipInAmount, chipInAmountLocalized: i18n.localizeCurrency(chipInAmount, viewerCurrency.code) };
	}

	const banditVariantData = psfComboBandit.variant?.data;
	if (isPsfComboBanditData(banditVariantData)) {
		const { amount, amountLocalized } = banditVariantData;
		return { chipInAmount: amount, chipInAmountLocalized: amountLocalized };
	}

	let chipInAmount = fcmValues.chipInAmount || CHIP_IN_USD_FALLBACK * viewerCurrency.exchangeRate;

	if (banditComboExpConfig?.enabled && banditComboExpConfig.bandit_amounts) {
		const { bandit_amounts: banditAmounts = [] } = banditComboExpConfig;
		const idx = Math.floor(Math.random() * banditAmounts.length);
		chipInAmount = banditAmounts[idx];
	}

	return { chipInAmount, chipInAmountLocalized: i18n.localizeCurrency(chipInAmount, viewerCurrency.code) };
}

function normalizePromotionGoal(utilityContext: UtilityContext, promoterCount: number) {
	const { i18n } = utilityContext;
	let divideCounter = 0;
	let tempPromoterCount = promoterCount;
	while (tempPromoterCount >= 10) {
		tempPromoterCount /= 10;
		divideCounter += 1;
	}
	const calculatedGoal = (Math.floor(tempPromoterCount) + 1) * 10 ** divideCounter;

	return {
		promotionGoal: calculatedGoal,
		promotionGoalLocalized: i18n.localizeNumber(calculatedGoal),
	};
}
